/**********************************************************************
* fetch-weather.js - Get Weather Data from Environment Canada
* By: Tyler Heshka < tyler.h@floralacres.ca >
*
* Version: 2.0
* Release Date: 2026-04-11
* License: Commercial. Licensed to Central Botanical Growers Ltd. o/a
* Floral Acres Greenhouses & Garden Centre - https://floralacres.ca/
*
* This script fetches structured weather data directly from the
* Environment and Climate Change Canada GeoMet API.
*
************************************************************************/

(function () {
  var listenersBound = false;

  function getWidgets() {
    return document.querySelectorAll('[data-weather-widget]');
  }

  function getField(widget, fieldName) {
    return widget.querySelector('[data-weather-field="' + fieldName + '"]');
  }

  function setText(widget, fieldName, value) {
    var element = getField(widget, fieldName);

    if (element) {
      element.textContent = value || '';
    }
  }

  function setHTML(widget, fieldName, value) {
    var element = getField(widget, fieldName);

    if (element) {
      element.innerHTML = value || '';
    }
  }

  function formatTemperature(value, units) {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return value + '°' + (units || 'C');
  }

  function formatObservedAt(timestamp, stationName) {
    if (!timestamp) {
      return '';
    }

    var date = new Date(timestamp);

    if (isNaN(date.getTime())) {
      return stationName ? 'Observed at ' + stationName : '';
    }

    var formatter = new Intl.DateTimeFormat('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });

    var observed = formatter.format(date);

    if (stationName) {
      return 'Observed at ' + stationName + ' • ' + observed;
    }

    return 'Observed ' + observed;
  }

  function pickForecast(forecasts, matcher) {
    if (!forecasts || !forecasts.length) {
      return null;
    }

    for (var index = 0; index < forecasts.length; index += 1) {
      var forecast = forecasts[index];
      var name = forecast && forecast.period && forecast.period.textForecastName && forecast.period.textForecastName.en;

      if (name && matcher(name.toLowerCase())) {
        return forecast;
      }
    }

    return null;
  }

  function summarizeForecast(forecast, tempClass) {
    if (!forecast) {
      return '';
    }

    var label = forecast.period && forecast.period.textForecastName && forecast.period.textForecastName.en;
    var temperatures = forecast.temperatures && forecast.temperatures.temperature;
    var summary = forecast.abbreviatedForecast && forecast.abbreviatedForecast.textSummary && forecast.abbreviatedForecast.textSummary.en;
    var temperatureText = '';

    if (temperatures && temperatures.length) {
      for (var index = 0; index < temperatures.length; index += 1) {
        var temperature = temperatures[index];
        var klass = temperature && temperature.class && temperature.class.en;

        if (klass === tempClass) {
          temperatureText = (tempClass === 'high' ? 'High ' : 'Low ') + formatTemperature(
            temperature.value && temperature.value.en,
            temperature.units && temperature.units.en
          );
          break;
        }
      }
    }

    if (!label && !summary && !temperatureText) {
      return '';
    }

    var parts = [];

    if (summary) {
      parts.push(summary);
    }

    if (temperatureText) {
      parts.push(temperatureText);
    }

    return label + ': ' + parts.join('. ');
  }

  function renderAlerts(warnings) {
    if (!warnings || !warnings.length) {
      return '<span class="weather-alert-none">No watches or warnings in effect.</span>';
    }

    var alert = warnings[0];
    var type = alert.type && alert.type.en ? alert.type.en : 'Weather alert';
    var description = alert.description && alert.description.en ? alert.description.en : '';

    return '<span class="weather-alert-warning">Warning: ' + type + (description ? ': ' + description : '') + '</span>';
  }

  function renderImage(widget, iconUrl, altText) {
    if (!iconUrl) {
      return;
    }

    setHTML(
      widget,
      'image',
      '<img src="' + iconUrl + '" alt="' + (altText || '') + '" style="width: 100%" />'
    );
  }

  function handleData(widget, data) {
    if (!data || !data.properties) {
      throw new Error('Unexpected GeoMet payload');
    }

    var properties = data.properties;
    var current = properties.currentConditions || {};
    var forecasts = properties.forecastGroup && properties.forecastGroup.forecasts;
    var todayForecast = pickForecast(forecasts, function (name) {
      return name === 'today' || name.indexOf('today') !== -1;
    }) || (forecasts && forecasts[0]);
    var tonightForecast = pickForecast(forecasts, function (name) {
      return name.indexOf('tonight') !== -1 || name.indexOf('night') !== -1;
    }) || (forecasts && forecasts[1]);
    var cityName = properties.name && properties.name.en ? properties.name.en : 'Saskatoon';
    var condition = current.condition && current.condition.en ? current.condition.en : '';
    var temperatureValue = current.temperature && current.temperature.value && current.temperature.value.en;
    var temperatureUnits = current.temperature && current.temperature.units && current.temperature.units.en;
    var currentTemperature = formatTemperature(temperatureValue, temperatureUnits);
    var stationName = current.station && current.station.value && current.station.value.en;
    var iconUrl = current.iconCode && current.iconCode.url;
    var observedAt = current.timestamp && current.timestamp.en;

    renderImage(widget, iconUrl, condition || cityName + ' weather');
    setText(widget, 'title', 'City of ' + cityName);
    setText(widget, 'current-temp', currentTemperature);
    setText(widget, 'current-detail', condition + (currentTemperature ? ', ' + currentTemperature : ''));
    setText(widget, 'daytime-high', summarizeForecast(todayForecast, 'high'));
    setText(widget, 'nighttime-low', summarizeForecast(tonightForecast, 'low'));
    setHTML(widget, 'current-alerts', renderAlerts(properties.warnings));
    setText(widget, 'observed-at', formatObservedAt(observedAt, stationName));
  }

  function renderUnavailable(widget) {
    setText(widget, 'title', 'City of Saskatoon');
    setText(widget, 'current-temp', '');
    setText(widget, 'current-detail', 'Weather data is temporarily unavailable.');
    setText(widget, 'daytime-high', '');
    setText(widget, 'nighttime-low', '');
    setText(widget, 'observed-at', '');
    setHTML(widget, 'current-alerts', '<span class="weather-alert-none">Weather service unavailable.</span>');
    setHTML(widget, 'image', '');
  }

  function loadWeatherForWidget(widget) {
    var cityIdentifier = widget.getAttribute('data-weather-city-identifier') || 'sk-40';
    var weatherEndpoint = 'https://api.weather.gc.ca/collections/citypageweather-realtime/items/' + encodeURIComponent(cityIdentifier) + '?f=json&lang=en';

    fetch(weatherEndpoint)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Weather request failed with status ' + response.status);
        }

        return response.json();
      })
      .then(function (data) {
        handleData(widget, data);
      })
      .catch(function (error) {
        console.error('Error loading weather data:', error);
        renderUnavailable(widget);
      });
  }

  function loadWeather() {
    var widgets = getWidgets();

    if (!widgets.length) {
      return;
    }

    for (var index = 0; index < widgets.length; index += 1) {
      loadWeatherForWidget(widgets[index]);
    }
  }

  function bindListeners() {
    if (listenersBound) {
      return;
    }

    listenersBound = true;

    document.addEventListener('shopify:section:load', loadWeather);
    document.addEventListener('shopify:section:reorder', loadWeather);
  }

  bindListeners();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadWeather, { once: true });
  } else {
    loadWeather();
  }
}());